<script setup lang="ts">
/**
 * The signed-in user's own spend meter (ADR 0049) — a compact pill in the
 * top nav that expands on hover/click into day + month detail.
 *
 * Always visible while budgeting is on, showing percent-used and time to
 * reset. An earlier version stayed hidden below the warn threshold on the
 * theory that a quiet UI is a kind one; that was wrong. If the number only
 * appears once you are in trouble, the first time you ever see it is the
 * moment it is bad news, and you have no sense of what normal looks like.
 * A standing meter is how someone learns their own usage.
 *
 * It escalates rather than appears: neutral under the warn threshold, amber
 * past it, red once a limit is exhausted.
 *
 * Refreshed on a slow poll, on window focus, and on demand via `refresh()`
 * — App.vue calls that when a chat turn ends, which is the only moment the
 * number can actually have moved for this user.
 */
import { computed, onMounted, onUnmounted, ref } from "vue";
import { type BudgetStatus, getMyBudget } from "../api/admin";

const props = defineProps<{ devUser?: string }>();

type Loaded = { enabled: true; warn_at: number } & BudgetStatus;

const state = ref<Loaded | null>(null);
const open = ref(false);
let timer: number | undefined;

async function refresh(): Promise<void> {
  try {
    const r = await getMyBudget(props.devUser);
    state.value = r.enabled ? (r as Loaded) : null;
  } catch {
    // A meter is not worth a toast. If the endpoint is unreachable (older
    // backend image, transient 5xx) the pill simply stays hidden.
    state.value = null;
  }
}

onMounted(() => {
  void refresh();
  timer = window.setInterval(refresh, 120_000);
  window.addEventListener("focus", refresh);
});
onUnmounted(() => {
  if (timer) window.clearInterval(timer);
  window.removeEventListener("focus", refresh);
});

/** Visible whenever budgeting is switched on for this principal. `null`
 * means the platform has budgeting off (or the meter could not load), in
 * which case there is nothing honest to show. */
const visible = computed(() => state.value !== null);

/** Which period is closest to its ceiling — that is the one worth showing a
 * countdown for, since it is the one that will bite first. */
const driver = computed<"daily" | "monthly">(() => {
  const s = state.value;
  if (!s) return "daily";
  const d = s.day_limit ? s.day_spend / s.day_limit : 0;
  const m = s.month_limit ? s.month_spend / s.month_limit : 0;
  return m > d ? "monthly" : "daily";
});

const driverReset = computed(() =>
  driver.value === "monthly"
    ? state.value?.month_reset_at
    : state.value?.day_reset_at,
);

const level = computed(() => {
  const s = state.value;
  if (!s) return "ok";
  if (s.exceeded) return s.enforced ? "over" : "warn";
  return s.warn ? "warn" : "ok";
});

const pct = computed(() =>
  Math.min(100, Math.round((state.value?.worst_fraction ?? 0) * 100)),
);

function money(n: number | null | undefined): string {
  if (n === null || n === undefined) return "no limit";
  return `${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${state.value?.currency ?? ""}`.trim();
}

/** "in 3 hours" / "on 1 Oct" — the reset instant matters more than the
 * exact duration, so anything past a day is shown as a date. */
function resetsIn(iso: string): string {
  const then = new Date(iso).getTime();
  const ms = then - Date.now();
  if (!Number.isFinite(ms)) return "";
  if (ms <= 0) return "shortly";
  const hours = ms / 3_600_000;
  if (hours < 1) return `in ${Math.max(1, Math.round(ms / 60_000))} min`;
  if (hours < 24) return `in ${Math.round(hours)} h`;
  return `on ${new Date(then).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  })}`;
}

/** Compact countdown for the pill — "5h", "12d", "40m". The panel spells it
 * out; the pill has no room. */
function shortReset(iso: string | undefined): string {
  if (!iso) return "";
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return "now";
  const h = ms / 3_600_000;
  if (h < 1) return `${Math.max(1, Math.round(ms / 60_000))}m`;
  if (h < 48) return `${Math.round(h)}h`;
  return `${Math.round(h / 24)}d`;
}

/** Expose a manual refresh so the chat path can update the number the
 * moment a turn ends — that is the only time it moves for this user. */
defineExpose({ refresh });

const summary = computed(() => {
  const s = state.value;
  if (!s) return "";
  if (s.exceeded === "daily") {
    return s.enforced
      ? `Daily limit reached — resets ${resetsIn(s.day_reset_at)}`
      : `Over your daily limit (not enforced)`;
  }
  if (s.exceeded === "monthly") {
    return s.enforced
      ? `Monthly limit reached — resets ${resetsIn(s.month_reset_at)}`
      : `Over your monthly limit (not enforced)`;
  }
  const which = driver.value === "monthly" ? "monthly" : "daily";
  return `${pct.value}% of your ${which} AI budget used — resets ${resetsIn(
    driverReset.value ?? "",
  )}`;
});
</script>

<template>
  <div v-if="visible && state" class="budget-meter" :class="`is-${level}`">
    <button
      class="budget-meter__pill"
      :title="summary"
      :aria-expanded="open"
      @click="open = !open"
    >
      <span class="budget-meter__track">
        <span class="budget-meter__fill" :style="{ width: `${pct}%` }"></span>
      </span>
      <span class="budget-meter__pct">{{ pct }}%</span>
      <span class="budget-meter__reset">· {{ shortReset(driverReset) }}</span>
    </button>
    <div v-if="open" class="budget-meter__panel">
      <p class="budget-meter__summary">{{ summary }}</p>
      <dl>
        <div>
          <dt>Today</dt>
          <dd>
            {{ money(state.day_spend) }} of {{ money(state.day_limit) }}
            <small>resets {{ resetsIn(state.day_reset_at) }}</small>
          </dd>
        </div>
        <div>
          <dt>This month</dt>
          <dd>
            {{ money(state.month_spend) }} of {{ money(state.month_limit) }}
            <small>resets {{ resetsIn(state.month_reset_at) }}</small>
          </dd>
        </div>
      </dl>
      <p class="budget-meter__foot">
        Estimated from model token prices. Ask an administrator if you need a
        higher limit.
      </p>
    </div>
  </div>
</template>

<style scoped>
.budget-meter {
  position: relative;
}
.budget-meter__pill {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-ink);
  font-size: 0.8rem;
  cursor: pointer;
}
.is-warn .budget-meter__pill {
  border-color: #d98324;
  color: #a4611a;
}
.is-over .budget-meter__pill {
  border-color: var(--color-error);
  color: var(--color-error);
}
.budget-meter__track {
  display: inline-block;
  width: 46px;
  height: 5px;
  border-radius: 3px;
  background: var(--color-surface-alt);
  overflow: hidden;
}
.budget-meter__fill {
  display: block;
  height: 100%;
  background: #d98324;
}
.is-over .budget-meter__fill {
  background: var(--color-error);
}
.budget-meter__pct {
  font-variant-numeric: tabular-nums;
}
.budget-meter__reset {
  font-variant-numeric: tabular-nums;
  color: var(--color-muted);
}
/* Under the warn threshold the meter is information, not an alert: it keeps
   the nav's own colours so a healthy 12% does not read as a warning. */
.is-ok .budget-meter__fill {
  background: var(--color-accent);
}
.budget-meter__panel {
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  z-index: 40;
  width: 17rem;
  padding: var(--space-3);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: 0 8px 24px rgb(0 0 0 / 0.12);
  font-size: 0.85rem;
  color: var(--color-ink);
  text-align: left;
}
.budget-meter__summary {
  margin: 0 0 var(--space-2);
  font-weight: 600;
}
.budget-meter__panel dl {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.budget-meter__panel dt {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-muted);
}
.budget-meter__panel dd {
  margin: 0;
  font-variant-numeric: tabular-nums;
}
.budget-meter__panel dd small {
  display: block;
  color: var(--color-muted);
}
.budget-meter__foot {
  margin: var(--space-2) 0 0;
  font-size: 0.75rem;
  color: var(--color-muted);
}
</style>
